import {
  AlertFilled,
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  DollarOutlined,
  GroupOutlined,
  MoneyCollectOutlined,
  MoreOutlined,
  PlusCircleFilled,
  RocketOutlined,
} from "@ant-design/icons";
import {
  Button,
  Divider,
  Flex,
  Input,
  List,
  Popover,
  Select,
  Table,
} from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useFormik } from "formik";
import { TrashIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import * as Yup from "yup";
import { useCreateGroup, useMoveExpenseToMisc } from "../api/api";
import {
  useGetAccounts,
  useGetCategoriesOnly,
  useGetExpensesByCategory2,
  useGetExpensesBySubCategoryDiff2,
  useGetExpensesForMonth2,
  useGetExpensesTillDate,
  useGetExpensesTotal2,
  useGetGroups2,
  useGetGroupUsers2,
  useGetMiscExpenses,
  useGetSubscriptions,
} from "../api/api.get";
import DateComponent from "../components/date.component";
import ConfirmDialog from "../components/dialog/confirm.dialog";
import DeleteExpenseDialog from "../components/dialog/delete-expense.dialog";
import GroupDialog from "../components/dialog/group.dialog";
import SaveAsGroupExpenseDialog from "../components/dialog/saveas.group.expense.dialog";
import SaveAsLedgerExpenseDialog from "../components/dialog/saveas.ledger.expense.dialog";
import SaveAsTripExpenseDialog from "../components/dialog/saveas.trip.expense.dialog";
import ExpensesDrawerList from "../components/expenses.drawer";
import { useNotifications } from "../components/notifications/notification";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { UpgradeBanner } from "../components/upgrader.banner.component";
import { getMonthNameByIndex } from "../helpers/date.helper";
import { useAuthContext } from "../providers/auth.provider";
import { useCurrencyContext } from "../providers/currency.provider";
import { useGroupContext } from "../providers/groups.provider";
import { IAccount, IExpense2, IGroup2 } from "../types/types";
import { plans } from "../utils/constants";
import { capitalize, capitalizeFirst } from "../utils/functions";
import { toFixed, toFixedDisplay } from "../utils/number.utils";
import ExpenseViewModal from "./expenses/components/expense.view.modal";
import EstimatedSavingsCard from "./home/estimated.savings.card";
dayjs.extend(utc);

export default function HomePage() {
  const { data: groupsData, refetch: refetchGroups } = useGetGroups2();
  const { data: accountsData } = useGetAccounts();
  const { defaultGroupId } = useGroupContext();
  const [date, setDate] = useState<dayjs.Dayjs>(dayjs());
  const navigate = useNavigate();

  const { currency } = useCurrencyContext();

  const currentGroup: IGroup2 | undefined = useMemo(() => {
    if (defaultGroupId && groupsData) {
      return groupsData.find((g) => g.id === defaultGroupId);
    }
    return undefined;
  }, [defaultGroupId, groupsData]);

  const handleDateChange = (date: dayjs.Dayjs) => {
    setDate(date);
  };

  const { data: subscriptionsData } = useGetSubscriptions();

  const { data: tillDateData } = useGetExpensesTillDate(defaultGroupId);
  const { data: currentMonthTotal } = useGetExpensesTotal2(
    defaultGroupId || "",
    date.startOf("M").format("YYYY-MM-DD"),
    date.endOf("M").format("YYYY-MM-DD"),
  );

  const { data: prevMonthTotal } = useGetExpensesTotal2(
    defaultGroupId || "",
    date.subtract(1, "M").startOf("M").format("YYYY-MM-DD"),
    date.subtract(1, "M").endOf("M").format("YYYY-MM-DD"),
  );

  const { data: miscExpenses } = useGetMiscExpenses(
    defaultGroupId,
    dayjs(date).startOf("M").format("YYYY-MM-DD"),
    dayjs(date).endOf("M").format("YYYY-MM-DD"),
  );

  const { data: expensesByCategoryData } = useGetExpensesByCategory2(
    defaultGroupId,
    date.startOf("M").format("YYYY-MM-DD"),
    date.endOf("M").format("YYYY-MM-DD"),
  );

  const { data: expensesBySubCategoryData2 } = useGetExpensesBySubCategoryDiff2(
    defaultGroupId,
    dayjs(date).format("YYYY-MM-DD"),
  );

  const spendingByCategory = useMemo(() => {
    if (!expensesByCategoryData) return [];
    return expensesByCategoryData.map((item: any) => ({
      name: item.category,
      value: toFixed(item.amount),
    }));
  }, [expensesByCategoryData]);

  const spendingBySubCategory2 = useMemo(() => {
    if (!expensesBySubCategoryData2) return [];
    return expensesBySubCategoryData2.data.map((item: any) => ({
      name: item.subcategory,
      value: item.thisMonthTotal,
      prevValue: item.prevMonthTotal,
    }));
  }, [expensesBySubCategoryData2]);

  // Sample monthly spending trend
  const monthlyTrend = useMemo(() => {
    if (!tillDateData) return [];
    return tillDateData
      .slice(0, 6)
      .map((item) => ({
        month: capitalize(item.monthName),
        spending: item.total,
      }))
      .reverse();
  }, [tillDateData]);

  // Sample detailed expense list
  const isCurrentMonthYear = useMemo(
    () => dayjs(date).isSame(dayjs(), "M"),
    [date],
  );

  const COLORS = ["#5B5BFF", "#8B7FFF", "#B5AAFF", "#DED7FF", "#E8E3FF"];

  const [groupsDialogVisible, showGroupsDialog] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState<string>();

  const { data: categoryFilteredExpense } = useGetExpensesForMonth2(
    defaultGroupId || "",
    date.startOf("M").format("YYYY-MM-DD"),
    date.endOf("M").format("YYYY-MM-DD"),
    selectedCategory,
  );

  useEffect(() => {
    if (!currentGroup && groupsData && groupsData.length > 0) {
      showGroupsDialog(true);
    } else {
      showGroupsDialog(false);
    }
  }, [currentGroup, groupsData]);

  const currentToPrevMonthTotalDiff = useMemo(() => {
    if (currentMonthTotal && prevMonthTotal) {
      return toFixed(currentMonthTotal.total - prevMonthTotal.total);
    }
    return 0;
  }, [currentMonthTotal, prevMonthTotal]);

  const currentPlan = useMemo(() => {
    if (subscriptionsData && subscriptionsData.length > 0) {
      return plans.find((p) => p.id === subscriptionsData[0].entitlement);
    }
    return undefined;
  }, [subscriptionsData]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-2 mt-8 mb-16">
      {groupsDialogVisible && (
        <GroupDialog
          open={groupsDialogVisible}
          onCancel={() => showGroupsDialog(false)}
          onOk={() => showGroupsDialog(false)}
        />
      )}

      <ExpensesDrawerList
        open={!!selectedCategory}
        onClose={() => setSelectedCategory(undefined)}
        title={selectedCategory}
        expenses={categoryFilteredExpense}
      />

      {groupsData?.length === 0 && (
        <CreateGroupSection onSuccess={() => refetchGroups()} />
      )}

      {groupsData && groupsData.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-5">
            <DateComponent onChange={(date) => handleDateChange(date)} />

            {!defaultGroupId && (
              <Button type="link" onClick={() => showGroupsDialog(true)} danger>
                Please Click Here To Select Group
              </Button>
            )}

            <div className="flex gap-2 items-center">
              <Button
                type="primary"
                icon={<PlusCircleFilled />}
                onClick={() => navigate("/expenses/create")}
              >
                Add Expense
              </Button>
              <span
                onClick={() => navigate(`/subscriptions`)}
                className={`px-4 py-1 cursor-pointer ${!!subscriptionsData ? "bg-gradient-to-r from-amber-300 to-amber-500" : "bg-gray-300"}`}
              >
                {currentPlan ? currentPlan.name : "FREE"}
              </span>
            </div>
          </div>
          <Divider />

          <div>
            <UpgradeBanner />

            {defaultGroupId && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-8">
                {/* Card 1: Summary */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Summary
                    </CardTitle>
                    <CardDescription>Key metrics overview</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="border-b pb-4">
                        <p className="text-sm text-muted-foreground mb-1">
                          {getMonthNameByIndex(date.month()) +
                            " " +
                            date.year()}
                        </p>
                        <p className="text-3xl font-bold text-foreground">
                          {currency}
                          {toFixedDisplay(currentMonthTotal?.total)}
                        </p>
                      </div>
                      <div className="border-b pb-4">
                        <p className="text-sm text-muted-foreground mb-1">
                          {dayjs(date).subtract(1, "M").format("MMMM")}{" "}
                          {dayjs(date).subtract(1, "M").format("YYYY")}
                        </p>
                        <p className="text-3xl font-bold text-primary">
                          {currency}
                          {toFixedDisplay(prevMonthTotal?.total)}
                        </p>
                      </div>

                      {isCurrentMonthYear && (
                        <div className="border-b pb-4">
                          <p className="text-sm text-muted-foreground mb-1">
                            Difference
                          </p>
                          <p className="flex align-center text-2xl font-bold text-foreground">
                            {currentToPrevMonthTotalDiff > 0 ? (
                              <ArrowUpOutlined
                                style={{ color: "red", marginRight: 8 }}
                              />
                            ) : (
                              <ArrowDownOutlined
                                style={{ color: "green", marginRight: 8 }}
                              />
                            )}

                            {toFixedDisplay(
                              Math.abs(currentToPrevMonthTotalDiff),
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Card 2: Estimated savings (income - expenses) */}
                <EstimatedSavingsCard expenseTotal={currentMonthTotal?.total} />

                {/* Card 3: Spending by Category - Pie Chart */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Spending by Category
                    </CardTitle>
                    <CardDescription>Distribution of expenses</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={spendingByCategory}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value }) => `${name}: $${value}`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {spendingByCategory.map(
                            (entry: any, index: number) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                                onClick={() => setSelectedCategory(entry.name)}
                              />
                            ),
                          )}
                        </Pie>
                        <Tooltip formatter={(value) => `$${value}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Card 3: Miscellaneous List */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      One Time Expenses
                    </CardTitle>
                    <CardDescription>Non-recurring expenses.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col">
                      <div className="flex justify-end ">
                        <Link className="text-blue-500 text-sm" to={"/misc"}>
                          View All
                        </Link>
                      </div>

                      <div
                        className="space-y-2"
                        style={{ overflowY: "auto", maxHeight: 320 }}
                      >
                        <List
                          itemLayout="horizontal"
                          dataSource={miscExpenses}
                          renderItem={(item, index) => (
                            <List.Item key={index}>
                              <List.Item.Meta
                                title={
                                  <Link to={`/expenses/${item.id}`}>
                                    <strong>
                                      {currency}
                                      {item.amount}
                                    </strong>
                                  </Link>
                                }
                                description={
                                  dayjs(item.expenseDate).format(
                                    "DD MMM YYYY",
                                  ) +
                                  " " +
                                  item.comments
                                }
                              />
                            </List.Item>
                          )}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Card 5: Monthly Spending Trend - Line Chart */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Monthly Trend
                    </CardTitle>
                    <CardDescription>
                      Till Date - Last 3 months spending
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={monthlyTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => `$${value}`} />
                        <Line
                          type="monotone"
                          dataKey="spending"
                          stroke="#5B5BFF"
                          strokeWidth={2}
                          dot={{ fill: "#5B5BFF", r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Card 6: Spending By Sub Categories */}
                <Card className="bg-white">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Spending By Sub Categories
                    </CardTitle>
                    <CardDescription>
                      Compare spending with previous month
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={spendingBySubCategory2}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip formatter={(value) => `$${value}`} />
                        <Bar dataKey="value" fill="#5B5BFF" />
                        <Bar dataKey="prevValue" fill="#82ca9d" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Card 4: Summary Metrics */}
              </div>
            )}
          </div>

          <Divider />

          {defaultGroupId && (
            <ExpensesListView accounts={accountsData} _date={date} />
          )}
        </div>
      )}
    </div>
  );
}

function CreateGroupSection({ onSuccess }: { onSuccess: () => void }) {
  const { mutateAsync } = useCreateGroup();
  const { notification, notifyError } = useNotifications();

  const formik = useFormik({
    initialValues: {
      name: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please enter a group name"),
    }),
    onSubmit: async (values) => {
      mutateAsync({
        name: values.name,
      })
        .then(async () => {
          onSuccess();
        })
        .catch(() => notifyError("Whoops! Please try again."));
    },
  });

  return (
    <Card className="border-0 shadow-2xl mt-35 mb-40">
      {notification}
      <CardHeader className="bg-gradient-to-r from-primary/5 to-blue-500/5 border-b">
        <CardTitle className="text-2xl">Create Your Group</CardTitle>
        <CardDescription>
          Set up your first group where you can track shared expenses
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-8">
        {/* Step 1: Group Setup */}
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <AlertFilled className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Why create a group?</p>
              <p>
                A group helps you organize and aggregate expenses. You can
                optionally add members such as your spouse or parents.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold mb-2 block">
              Group Name <span className="text-red-500">*</span>
            </span>
            <Input
              id="groupName"
              {...formik.getFieldProps("name")}
              placeholder="e.g., My Family, Roommates, Friends Trip"
              className={`h-11`}
            />
            {!!formik.errors.name && (
              <p className="text-xs text-red-500">{formik.errors.name}</p>
            )}
          </div>

          {/* Info Box */}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-8">
          <Button
            type="primary"
            onClick={formik.submitForm}
            className={`flex-1`}
          >
            {"Create Group"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ExpensesListView({
  _date,
  accounts,
}: {
  accounts?: IAccount[];
  _date: dayjs.Dayjs;
}) {
  const { defaultGroupId } = useGroupContext();
  const { notification, notifyError, notifySuccess } = useNotifications();
  const { data: categoriesOnlyData } = useGetCategoriesOnly();
  const { data: groupUsersData } = useGetGroupUsers2(defaultGroupId);

  const memberNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    groupUsersData?.forEach((u) => {
      map[u.username] = u.firstName ?? "";
    });
    return map;
  }, [groupUsersData]);

  const {
    data: expensesListData,
    isPending: isExpenseListLoading,
    refetch,
  } = useGetExpensesForMonth2(
    defaultGroupId || "",
    _date.startOf("M").format("YYYY-MM-DD"),
    _date.endOf("M").format("YYYY-MM-DD"),
  );

  useEffect(() => {
    if (_date) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_date]);

  const { mutateAsync: saveAsMiscExpenseAsync } =
    useMoveExpenseToMisc(defaultGroupId);

  const { currency } = useCurrencyContext();
  const { user } = useAuthContext();

  const [saveAsMiscExpense, setSaveAsMiscExpense] = useState<
    IExpense2 | undefined
  >(undefined);

  const [saveAsLedgerExpense, setSaveAsLedgerExpense] = useState<
    IExpense2 | undefined
  >(undefined);
  const [saveAsTripExpense, setSaveAsTripExpense] = useState<
    IExpense2 | undefined
  >(undefined);

  const [deleteExpense, setSaveAsDeleteExpense] = useState<
    IExpense2 | undefined
  >(undefined);

  const [saveAsanotherGroupExpense, setSaveAsAnotherGroupExpense] = useState<
    IExpense2 | undefined
  >(undefined);

  const columns = [
    {
      title: "Description",
      dataIndex: "comments",
      key: "comments",
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
    },
    {
      title: "Amount",
      key: "amount",
      render: (item: IExpense2) => (
        <button
          onClick={() => setViewExpense(item)}
          className="font-bold text-[#541eff]"
        >
          {currency}
          {toFixedDisplay(item.amount)}
        </button>
      ),
      sorter: (a: IExpense2, b: IExpense2) => a.amount - b.amount,
      sortDirections: ["descend", "ascend"] as any[],
    },
    {
      title: "Date",
      dataIndex: "expenseDate",
      key: "expenseDate",
      render: (date: string) => (
        <span>{dayjs.utc(date).format("DD MMM YYYY")}</span>
      ),
    },
    {
      title: "Card",
      key: "creditcard",
      render: (_: any, record: IExpense2) => (
        <span>{accountNameMap[record.creditcard] ?? "Cash"}</span>
      ),
    },
    {
      title: "Member",
      key: "username",
      render: (_: any, record: IExpense2) => (
        <span>{memberNameMap[record.username]}</span>
      ),
    },
    {
      title: "",
      width: 30,
      render: (_: any, record: IExpense2) => (
        <Popover
          content={
            <div className="flex flex-col justify-items-start gap-2">
              <Popover
                content={
                  <div className="flex flex-col justify-items-start gap-2">
                    <Button
                      type="link"
                      icon={<GroupOutlined />}
                      onClick={() => setSaveAsAnotherGroupExpense(record)}
                    >
                      New Group
                    </Button>
                    <Button
                      type="link"
                      icon={<MoneyCollectOutlined />}
                      onClick={() => {
                        setSaveAsMiscExpense(record);
                      }}
                    >
                      One Time
                    </Button>
                    <Button
                      type="link"
                      onClick={() => setSaveAsLedgerExpense(record)}
                      icon={<DollarOutlined />}
                    >
                      To Ledger
                    </Button>
                    <Button
                      type="link"
                      onClick={() => setSaveAsTripExpense(record)}
                      icon={<RocketOutlined />}
                    >
                      To Trip
                    </Button>
                  </div>
                }
              >
                <Button
                  icon={<ArrowRightOutlined />}
                  type="dashed"
                  disabled={record.username !== user?.username}
                >
                  Move To
                </Button>
              </Popover>

              <Button
                type="link"
                danger
                onClick={() => setSaveAsDeleteExpense(record)}
                icon={<TrashIcon className="text-sm" style={{ width: 15 }} />}
              >
                Delete
              </Button>
            </div>
          }
          trigger="hover"
        >
          <Button
            icon={<MoreOutlined />}
            type="dashed"
            disabled={record.username !== user?.username}
          />
        </Popover>
      ),
    },
  ];

  const handleSaveAsMiscExpense = (expense: IExpense2) => {
    saveAsMiscExpenseAsync({
      expenseId: expense.id,
    })
      .then(() => {
        notifySuccess({
          description: "Expense saved as Misc successfully",
        });

        refetch();
      })
      .catch(() => {
        notifyError("Something went wrong. Please try again.");
      })
      .finally(() => {
        setSaveAsMiscExpense(undefined);
      });
  };

  const [categoryFilter, setCategoryFilter] = useState<string>();
  const [searchCommentsText, setSearchCommentsText] = useState<string>("");
  const [accountFilter, setAccountFilter] = useState<string>();
  const [memberFilter, setMemberFilter] = useState<string>();

  const filteredExpenses = useMemo(() => {
    let filteredResults: any[] = [];
    if (!expensesListData) return [];

    if (expensesListData) {
      filteredResults = expensesListData;
      if (categoryFilter) {
        filteredResults = filteredResults.filter(
          (e: any) => e.category === categoryFilter,
        );
      }
      if (searchCommentsText) {
        filteredResults = filteredResults.filter((e: any) =>
          e.comments?.toLowerCase().includes(searchCommentsText.toLowerCase()),
        );
      }
      if (accountFilter) {
        filteredResults = filteredResults.filter(
          (e) => e.creditcard === accountFilter,
        );
      }
      if (memberFilter) {
        filteredResults = filteredResults.filter(
          (e) => e.username === memberFilter,
        );
      }

      return filteredResults;
    }
  }, [
    categoryFilter,
    expensesListData,
    searchCommentsText,
    accountFilter,
    memberFilter,
  ]);

  const categoriesOptions = useMemo(() => {
    const defaultOption = { label: "--Category--", value: "" };
    if (categoriesOnlyData) {
      const options = categoriesOnlyData.map((cat) => {
        return { label: cat, value: cat };
      });
      return [defaultOption, ...options];
    }
    return [defaultOption];
  }, [categoriesOnlyData]);

  const [viewExpense, setViewExpense] = useState<IExpense2>();

  const handleCloseModal = () => {
    setViewExpense(undefined);
    refetch();
  };

  const accountOptions = useMemo(() => {
    let options = [{ label: "Select Account", value: "" }];
    if (accounts) {
      options = [
        ...options,
        ...accounts.map((a) => {
          return { label: capitalizeFirst(a.name), value: a.id };
        }),
      ];
    }
    return options;
  }, [accounts]);

  // account/card id -> display name (expenses store the id in `creditcard`)
  const accountNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    accounts?.forEach((a) => {
      map[a.id] = capitalizeFirst(a.name);
    });
    return map;
  }, [accounts]);

  const memberOptions = useMemo(() => {
    const defaultOption = { label: "All Members", value: "" };
    if (groupUsersData) {
      return [
        defaultOption,
        ...groupUsersData.map((u) => ({
          label:
            [u.firstName, u.lastName].filter(Boolean).join(" ") || u.username,
          value: u.username,
        })),
      ];
    }
    return [defaultOption];
  }, [groupUsersData]);

  return (
    <Card className="bg-white">
      {viewExpense && (
        <ExpenseViewModal
          expense={viewExpense}
          onClose={() => handleCloseModal()}
        />
      )}
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between items-center">
          <Flex vertical gap={5}>
            <CardTitle>Recent Transactions</CardTitle>
            <CardDescription>All your recent transactions</CardDescription>
          </Flex>

          <div className="flex flex-wrap gap-2 mt-3 md:mt-0">
            <Select
              onChange={(value) => setAccountFilter(value)}
              options={accountOptions}
              placeholder={"Filter By Account"}
              className="flex-1 min-w-[130px]"
            />
            <Select
              allowClear
              onChange={(value: string) => setMemberFilter(value)}
              placeholder="Filter by member..."
              options={memberOptions}
              className="flex-1 min-w-[130px]"
            />
            <Input
              placeholder="Description..."
              value={searchCommentsText}
              onChange={(e) => setSearchCommentsText(e.target.value)}
              className="flex-1 min-w-[130px]"
            />
            <Select
              allowClear
              onChange={(value: string) => setCategoryFilter(value)}
              placeholder="Filter by category..."
              options={categoriesOptions}
              className="flex-1 min-w-[130px]"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div>
          {notification}
          {saveAsMiscExpense && (
            <ConfirmDialog
              open={!!saveAsMiscExpense}
              onCancel={() => setSaveAsMiscExpense(undefined)}
              onOk={() => handleSaveAsMiscExpense(saveAsMiscExpense!)}
              title={"Are you sure you want to convert this one time expense?"}
              subtitle="This expense will retain its amount value, date and comments. Category and subcategory will be removed."
            />
          )}
          {saveAsLedgerExpense && (
            <SaveAsLedgerExpenseDialog
              open={!!saveAsLedgerExpense}
              expense={saveAsLedgerExpense}
              onCancel={() => setSaveAsLedgerExpense(undefined)}
              onOk={async () => {
                setSaveAsLedgerExpense(undefined);
                notifySuccess({ description: "Expenses successfully moved" });
                await refetch();
              }}
            />
          )}
          {saveAsTripExpense && (
            <SaveAsTripExpenseDialog
              open={!!saveAsTripExpense}
              expense={saveAsTripExpense as any}
              onCancel={() => setSaveAsTripExpense(undefined)}
              onOk={async () => {
                setSaveAsTripExpense(undefined);
                notifySuccess({ description: "Expenses successfully moved" });
                await refetch();
              }}
            />
          )}
          {saveAsanotherGroupExpense && (
            <SaveAsGroupExpenseDialog
              open={!!saveAsanotherGroupExpense}
              expense={saveAsanotherGroupExpense}
              onCancel={() => setSaveAsAnotherGroupExpense(undefined)}
              onOk={async () => {
                setSaveAsAnotherGroupExpense(undefined);
                notifySuccess({ description: "Expenses successfully moved" });
                await refetch();
              }}
            />
          )}

          {deleteExpense && (
            <DeleteExpenseDialog
              expense={deleteExpense}
              open={!!deleteExpense}
              onCancel={() => setSaveAsDeleteExpense(undefined)}
              onOk={async () => {
                setSaveAsDeleteExpense(undefined);
                notifySuccess({
                  description: "Expenses successfully deleted.",
                });
                await refetch();
              }}
            />
          )}
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <Table
              dataSource={filteredExpenses}
              columns={columns}
              loading={isExpenseListLoading}
              pagination={{ pageSize: 50 }}
            />
          </div>

          {/* Mobile card list */}
          <div className="block md:hidden space-y-3">
            {isExpenseListLoading && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Loading…
              </div>
            )}
            {!isExpenseListLoading &&
              (!filteredExpenses || filteredExpenses.length === 0) && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No transactions found.
                </div>
              )}
            {filteredExpenses?.map((expense: IExpense2, idx: number) => (
              <div
                key={expense.id ?? idx}
                className="rounded-lg border border-border bg-white px-4 py-3 flex flex-col gap-1 shadow-sm"
              >
                <div className="flex justify-between items-start">
                  <button
                    className="text-lg font-bold text-[#541eff]"
                    onClick={() => setViewExpense(expense)}
                  >
                    {currency}
                    {toFixedDisplay(expense.amount)}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {dayjs.utc(expense.expenseDate).format("DD MMM YYYY")}
                  </span>
                </div>
                {expense.comments && (
                  <p className="text-sm text-foreground truncate">
                    {expense.comments}
                  </p>
                )}
                <div className="flex gap-2 flex-wrap mt-1">
                  {expense.category && (
                    <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                      {expense.category}
                    </span>
                  )}
                  {memberNameMap[expense.username] && (
                    <span className="text-xs bg-blue-50 text-blue-600 rounded-full px-2 py-0.5">
                      {memberNameMap[expense.username]}
                    </span>
                  )}
                  <span className="text-xs bg-purple-50 text-purple-600 rounded-full px-2 py-0.5">
                    {accountNameMap[expense.creditcard] ?? "Cash"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
